import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Row, Input, Divider } from 'antd';
import { FixedSizeList as List } from 'react-window';
import PropTypes from 'prop-types';

import { includesTerm } from '../../helpers/helpers';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import CustomExportFilterDropdownFooter from '../../reports/Exports/CustomExportFilterDropdownFooter';

export default function SearchFormFilterHeader({
  clearFilters,
  confirm,
  filters = [],
  selectedKeys,
  setSelectedKeys,
  prefixCls,
  visible,
  showTextSearch = false,
  hideList = false,
}) {
  const [stagedSelection, setStagedSelection] = useState([]);
  const [searchStr, setSearchStr] = useState();
  const [containsText, setContainsText] = useState('');

  const onSearchStrChanged = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
  },[])

  const onSelect = useCallback((checkKey) => (checked) => {
    if (checked) {
      setStagedSelection(stagedSelection.concat([checkKey]));
    } else {
      setStagedSelection(stagedSelection.filter((key) => key !== checkKey));
    }
  },[stagedSelection])

  const onContainsTextChanged = useCallback((e) => {
    const { target: { value } = {} } = e;
    setContainsText(value ?? '');
  }, []);

  const onConfirm = useCallback(() => {
    if (showTextSearch) {
      const textTrim = containsText.trim();
      if (!stagedSelection.length && !textTrim) {
        clearFilters();
      } else {
        setSelectedKeys([{
          values: Array.isArray(stagedSelection) ? stagedSelection : [],
          text: textTrim || null,
        }]);
      }
    } else {
      setSelectedKeys(stagedSelection);
    }
    confirm(true);
  }, [
    clearFilters,
    confirm,
    containsText,
    showTextSearch,
    setSelectedKeys,
    stagedSelection,
  ]);

  const onResetFilters = useCallback(() => {
    setStagedSelection([]);
    setContainsText('');
    setSearchStr();
    clearFilters();
    confirm(true);
  }, [clearFilters, confirm]);

  useEffect(() => {
    if (!visible) return;
    setSearchStr();
    if (showTextSearch) {
      if (selectedKeys?.length === 1) {
        const parsed = selectedKeys[0];
        setStagedSelection(parsed.values ?? []);
        setContainsText(parsed.text ?? '');
        return;
      }
      setStagedSelection([]);
      setContainsText('');
      return;
    }
    setStagedSelection(selectedKeys ?? []);
  }, [visible, selectedKeys, showTextSearch]);

  const checkSet = useMemo(() => new Set(stagedSelection),[stagedSelection]);

  const dataSource = useMemo(() => {
    if (!searchStr) return filters;
    return filters.filter((item) => includesTerm(item.text, searchStr));
  },[searchStr, filters]);

  return (
    <div
      className={prefixCls}
      style={{
        padding: '2px 5px',
        maxHeight: showTextSearch ? 430 : 370,
        width: 400,
      }}
    >
      {showTextSearch && (
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <div className="export-filter-dropdown-title">
            Search Text
          </div>
          <Input
            placeholder="Search column"
            value={containsText}
            onChange={onContainsTextChanged}
            allowClear
            style={{ marginBottom: !hideList ? 5 : 0 }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {!hideList && (
          <>
            <div className="export-filter-dropdown-title">
              Search Options
            </div>
            <Input.Search
              className='searchbar'
              placeholder="Search"
              onChange={onSearchStrChanged}
              allowClear
              style={{ marginBottom: 5 }}
              value={searchStr}
              onKeyDown={(e) => {
                /*
                  AnalyticsBreakDownTag uses this in a dropdown, inside a Select
                  If we dont stop propagation, the backspace is propagated to the Select
                  and the tag is removed from the selected values
                */
                e.stopPropagation();
              }}
            />
          </>
        )}
        {!hideList && (
          <>
            <Divider style={{ margin: 0 }} />
            <List
              height={Math.max(1, Math.min(dataSource.length * 25, 250))}
              itemCount={dataSource.length}
              itemSize={25}
              width={380}
            >
              {
                ({ index, style }) => {
                  const item = dataSource[index];
                  return (
                    <Row
                      style={{
                        padding: '2px 7px',
                        ...style,
                      }}
                    >
                      <OnTraccrCheckbox
                        key={item.value}
                        value={checkSet.has(item.value)}
                        onChange={onSelect(item.value)}
                        textStyle={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}

                        checkboxStyle={{
                          maxWidth: 365,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                        label={item.text}
                      />
                    </Row>
                  );
                }
              }
            </List>
          </>
        )}
        <Divider style={{ margin: 0 }} />
        <CustomExportFilterDropdownFooter
          onReset={onResetFilters}
          onApply={onConfirm}
          resetDisabled={!selectedKeys?.length}
        />
    </div>
  );
}

SearchFormFilterHeader.propTypes = {
  clearFilters: PropTypes.func.isRequired,
  confirm: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  })),
  selectedKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedKeys: PropTypes.func.isRequired,
  prefixCls: PropTypes.string,
  visible: PropTypes.bool,
  showTextSearch: PropTypes.bool,
  hideList: PropTypes.bool,
};

SearchFormFilterHeader.defaultProps = {
  filters: [],
  selectedKeys: [],
  prefixCls: undefined,
  visible: false,
  showTextSearch: false,
  hideList: false,
};
